import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { Store } from "@ngrx/store";
import { combineLatest, Observable, Subscription } from "rxjs";
import { Workspace } from "../../workspace/state/workspaces.model";
import { WorkspacesState } from "../../workspace/state/workspaces.reducer";
import { selectWorkspacesState } from "../../workspace/state/workspaces.selectors";
import { PadlockEventsService } from "../padlock-events.service";
import { PadlockService } from "../padlock.service";
import { ProvisionUser } from "../state/padlock-token.actions";
import { PadlockTokenState } from "../state/padlock-token.reducer";
import { selectPadlockTokenState } from "../state/padlock-token.selectors";
import { Location } from "@angular/common";

@Component({
  selector: "app-provision-user",
  templateUrl: "./provision-user.component.html",
  styleUrls: ["./provision-user.component.scss"],
  standalone: false,
})
export class ProvisionUserComponent implements OnInit, OnDestroy {
  workspacesState$: Observable<WorkspacesState> = this.store.select(
    selectWorkspacesState,
  );
  padlockTokenState$: Observable<PadlockTokenState> = this.store.select(
    selectPadlockTokenState,
  );
  stateSub: Subscription;
  eventSub: Subscription;

  email = new UntypedFormControl(null, [Validators.required, Validators.email]);

  PASSWORD_MIN_LENGTH = 4;
  password = new UntypedFormControl(null, [
    Validators.required,
    Validators.minLength(this.PASSWORD_MIN_LENGTH),
  ]);

  // Password visibility state
  hidePassword = true;

  workspaceOptions: WorkspaceOption[] = [];
  workspaces = new UntypedFormControl(null, []);

  EMAIL_FORM_KEY = "email";
  PASSWORD_FORM_KEY = "password";
  WORKSPACES_FORM_KEY = "workspaces";
  form: UntypedFormGroup = new UntypedFormGroup({
    [this.EMAIL_FORM_KEY]: this.email,
    [this.PASSWORD_FORM_KEY]: this.password,
    [this.WORKSPACES_FORM_KEY]: this.workspaces,
  });

  constructor(
    private store: Store<any>,
    private padlock: PadlockService,
    private events: PadlockEventsService,
    private location: Location,
  ) {}

  ngOnInit(): void {
    this.stateSub = combineLatest([
      this.workspacesState$,
      this.padlockTokenState$,
    ]).subscribe((results) => {
      const workspaces = results[0].workspaces;
      const decodedToken = this.padlock.getDecodedToken();
      const userID = decodedToken ? decodedToken.sub : null;
      const isPlatformAdmin = decodedToken?.platform_admin === true;

      if (!workspaces) return;

      // Create the workspace options
      this.workspaceOptions = workspaces
        // Map to option
        .map((workspace) => ({
          workspace,
          isOwner: workspace.owner === userID || isPlatformAdmin,
          selected: false,
        }))
        // Sort by workspace name alphabetically
        .sort((a: WorkspaceOption, b: WorkspaceOption) =>
          a.workspace.label > b.workspace.label ? 1 : -1,
        )
        // Sub sort by ownership
        .sort((a: WorkspaceOption, b: WorkspaceOption) =>
          a.isOwner && !b.isOwner ? -1 : 1,
        );

      // Set the form control's value to the options
      this.workspaces.setValue(this.workspaceOptions);
    });

    this.eventSub = this.events.userProvisioned$.subscribe(() => {
      // Clear the form on this event
      this.form.reset();

      // Reset the workspace options
      this.workspaces.setValue(this.workspaceOptions);
    });
  }

  ngOnDestroy() {
    this.stateSub?.unsubscribe();
    this.eventSub?.unsubscribe();
  }

  provisionUser() {
    const workspaceIDs = this.workspaces.value
      .filter((option) => option.selected)
      .map((option) => option.workspace.uuid);
    this.store.dispatch(
      new ProvisionUser(this.email.value, this.password.value, workspaceIDs),
    );
  }

  goBack() {
    this.location.back();
  }
}

interface WorkspaceOption {
  workspace: Workspace;
  isOwner: boolean;
  selected: boolean;
}
